import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { Departure } from '../models/departure';
import { ContactPlace } from '../models/contact-place';
import { WebApiService } from './web-api.service';
import { ConnectionDetail, MapStation } from '../models';
import { Connection, MapModel } from '../../main/map/models';
import { LanguageService } from './lang/language.service';
import { LanguageToTtLang } from './lang/language';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private readonly maxErrorsLogged = 100;
  private errorsLogged = 0;  
  private lastError: string = ""; 
  constructor(
    private readonly http: HttpClient,
    private readonly webApi: WebApiService,
    private readonly languageService: LanguageService) { }

  findConns(term: string) {
    return this.http.get<Connection[]>(`/oredo/findConns/${term}`)
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJsonArray(data, Connection)
        ));
  }
    
  getConn(id: string, crId: string | null = null, stationId: string | null = null, crT: string = "0", crD: string = "", getPath: boolean = false) {
    if (stationId)
      return this.getConnFromCrws(id, crId!, crT, stationId, crD, getPath);

    return this.http.get<ConnectionDetail>(`/oredo/detail/${id}`, { params: { geom: getPath } })
    .pipe(
      catchError(error => {
        this.webApi.handleError(error);
        throw error;
      }),
      map(data =>
        this.webApi.deserializeJson(data, ConnectionDetail)
      ));
  }

  getConnFromCrws(id: string, crId: string, crTtIndex: string, stationId: string, delayQuery: string, getPath: boolean = false) {
    const langId = LanguageToTtLang[this.languageService.language];
    let params: Record<string, any> = { crId: crId, crT: crTtIndex, langId: langId, crD: delayQuery, geom: getPath };

    return this.http.get<ConnectionDetail>(
      `/crws/detail/${id}/${stationId}`, { params: params })
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJson(data, ConnectionDetail)
        ));
  }

  getContactPlaces() {
    return this.http
      .get<ContactPlace[]>(`/contactPlaces/contactPlaces`)
      .pipe(
        catchError(error => {
          throw error;
        }));
  }

  findContactPlaces(term: string) {
    return this.http
      .get<[any, ContactPlace[]]>(`/contactPlaces/contactPlaces`, { params: { search: term } })
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJsonArray(data, ContactPlace)
        ));
  }

  getContactPlace(id: number) {
    return this.http
      .get<[any, ContactPlace]>(`/contactPlaces/contactPlace`, { params: { id: id } })
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJson(data, ContactPlace)
        ));
  }

  getStation(stopId: string) {
    return this.http
      .get<MapStation>(`/oredo/getStation`, { params: { id: stopId } })
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJson(data, MapStation)
        ));
  }

  findStations(search: string) {
    return this.http
      .get<[any, MapStation[]]>(`/oredo/findStations`, { params: { mask: search } })
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJsonArray(data, MapStation)
        ));
  }

  getDepartures(id: string, maxCount: number | null = null) {
    let obs: Observable<Departure[]>;
    const langId = LanguageToTtLang[this.languageService.language];
    if (maxCount)
      obs = this.http.get<Departure[]>(`/crws/departures/${id}`, { params: { maxCount: maxCount, langId: langId } });
    else
      obs = this.http.get<Departure[]>(`/crws/departures/${id}`, { params: { langId: langId } });

    return obs.pipe(
      catchError(error => {
        this.webApi.handleError(error);
        throw error;
      }),
      map(data =>
        this.webApi.deserializeJsonArray(data, Departure)
      ));
  }

  getMap() {
    return this.http
      .get<MapModel>('/map/map')
      .pipe(
        catchError(error => {
          this.webApi.handleError(error);
          throw error;
        }),
        map(data =>
          this.webApi.deserializeJson(data, MapModel)
        ));
  }

  getMapData(prms: string) {
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    const obs = this.http.post<MapModel>('/map/mapData', prms, { headers: reqHeaders });

    return obs.pipe(
      catchError(error => {
        this.webApi.handleError(error);
        throw error;
      }),
      map(data =>
        this.webApi.deserializeJson(data, MapModel)
      ));
  }

  logErrorOnServer(error: string) {
    if (error == this.lastError || this.errorsLogged >= this.maxErrorsLogged) {
      return new Observable<Object>;
    }

    this.lastError = error;
    this.errorsLogged++;
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    const obs = this.http.post<any>('/error/logClientError', error, { headers: reqHeaders });
    return obs.pipe(
      catchError(error => this.webApi.handleError(error)));
  }
}
